/**
 * In order to use refetchOnWindowFocus from React Query when you have an iframe on the page
 * and you don't want to refetch the data when you change the focus from the iframe to another element on the window.
 *
 * Modified Gist from https://gist.github.com/tannerlinsley/1d3a2122332107fcd8c9cc379be10d88
 * Recomended by react query here https://react-query.tanstack.com/guides/window-focus-refetching#ignoring-iframe-focus-events
 *
 * PS: the event listeners for the iframe was not working as expect so I adapted the code from the gist
 */

type State = {
  added: boolean;
  interval: false | ReturnType<typeof setInterval>;
  inFrame: boolean;
  callbacks: Array<SetFocusedCallback>;
};

type SetFocusedCallback = (focused?: boolean) => void;

const state: State = {
  added: false,
  interval: false,
  inFrame: false,
  callbacks: [],
};

export const onWindowFocus = (newCallback: SetFocusedCallback) => {
  state.callbacks.push(newCallback);
  start();
  return () => {
    state.callbacks = state.callbacks.filter(
      (registeredCallback) => registeredCallback !== newCallback
    );
    stop();
  };
};

function handleInIframe(iframe: HTMLIFrameElement) {
  if (document.activeElement === iframe) {
    state.inFrame = true;
  } else {
    state.inFrame = false;
  }
}

const runIFrameCheck = () => {
  const iframes = Array.from(document.getElementsByTagName("iframe"));
  // console.debug("Polling iframes... found: ", iframes.length);

  iframes.forEach((iframe) => {
    handleInIframe(iframe);
  });
};

const start = () => {
  if (state.interval) {
    clearInterval(state.interval);
  }

  if (!state.added) {
    state.added = true;
    window.addEventListener("focus", () => {
      if (state.inFrame) {
        state.inFrame = false;
        return;
      }

      state.callbacks.forEach((callback) => callback(true));
    });
  }

  state.interval = setInterval(runIFrameCheck, 500);
};

const stop = () => {
  if (!state.callbacks.length && state.interval) {
    clearInterval(state.interval);
  }
};
